/**
 * Encrypts a password using AES-CBC with a secret key.
 * @param {string} password - The password to encrypt.
 * @param {string} decryptedSecretKey - The secret key for encryption.
 * @returns {Promise<string>} - The encrypted password in Base64 format (includes IV).
 */
export const passwordEncrypt = async (password, decryptedSecretKey) => {
    if (!decryptedSecretKey) throw new Error("Secret key not available");

    let keyString = decryptedSecretKey;

    if (keyString.length !== 16 && keyString.length !== 24 && keyString.length !== 32) {
      keyString = keyString.padEnd(32, '0').slice(0, 32);
    }

    const encoder = new TextEncoder();
    const keyData = encoder.encode(keyString);

    const key = await window.crypto.subtle.importKey(
      "raw",
      keyData,
      { name: "AES-CBC" },
      false,
      ["encrypt"]
    );

    const data = encoder.encode(password);
    const iv = window.crypto.getRandomValues(new Uint8Array(16));

    const encryptedBuffer = await window.crypto.subtle.encrypt(
      { name: "AES-CBC", iv },
      key,
      data
    );

    const bufferToBase64 = (buffer) => window.btoa(String.fromCharCode(...new Uint8Array(buffer)));

    return `${bufferToBase64(iv)}:${bufferToBase64(encryptedBuffer)}`;
  };
  